import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TranscriptDisplay } from '@/entities/call'
import { useLastTestResult } from '@/hooks/useSimulations'
import { messagesToTranscript } from '@/shared/lib/messagesToTranscript'

interface LastTestResultProps {
  testId: number
  maxTurns?: number
}

export function LastTestResult({ testId, maxTurns }: LastTestResultProps) {
  const { data, isLoading } = useLastTestResult(testId)

  if (isLoading || !data?.run) return null

  const { result, messages } = data
  const isRunning = result?.status === 'running' || data.run.status === 'running' || data.run.status === 'pending'
  const isPassed = result?.status === 'passed'
  const isFailed = result?.status === 'failed' || result?.status === 'error'
  const transcriptEvents = messagesToTranscript(messages)
  const turnCount = messages.filter((m: any) => m.role === 'user').length

  return (
    <Card className="border-gray-300">
      <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-body font-semibold">Last Test Result</CardTitle>
          {isPassed && <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Passed</Badge>}
          {isFailed && <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Failed</Badge>}
          {isRunning && <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Running</Badge>}
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {isRunning && transcriptEvents.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-4">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
            <p className="text-detail text-blue-600">Running... Turn {turnCount || 1}/{maxTurns ?? '?'}</p>
          </div>
        ) : isRunning && transcriptEvents.length > 0 ? (
          <>
            <div className="bg-white rounded border border-gray-100 p-1">
              <TranscriptDisplay transcript={JSON.stringify(transcriptEvents)} />
            </div>
            <div className="flex items-center gap-2">
              <div className="animate-spin h-3 w-3 border-2 border-blue-500 border-t-transparent rounded-full" />
              <p className="text-detail text-blue-600">Running... Turn {turnCount}/{maxTurns ?? '?'}</p>
            </div>
          </>
        ) : transcriptEvents.length === 0 ? (
          <p className="text-detail text-gray-400 text-center py-4">No messages yet</p>
        ) : (
          <div className="bg-white rounded border border-gray-100 p-1">
            <TranscriptDisplay transcript={JSON.stringify(transcriptEvents)} />
          </div>
        )}

        {result?.evaluation && (
          <div className="space-y-1">
            <p className="text-detail font-medium text-gray-700">Evaluation</p>
            <p className="text-detail text-gray-600 whitespace-pre-wrap">{result.evaluation}</p>
          </div>
        )}

        {result?.error && (
          <div className="space-y-1">
            <p className="text-detail font-medium text-red-700">Error</p>
            <p className="text-detail text-red-600">{result.error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
